import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Paper,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Help as HelpIcon
} from '@mui/icons-material'
import SEO from '@components/SEO'
import { useBetaMode } from '@hooks/useBetaMode'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { updateUser } from '@store/slices/authSlice'
import { useGetPlansQuery, useSelectPlanMutation } from '@store/api/subscriptionApi'
import { OFFLINE_ANSWER, REFUNDS_ANSWER } from '@/content/productFaqCopy'
import { MARKETING_PLANS } from '@/content/marketingPlans'

type PlanUiMeta = {
  description: string
  popular: boolean
  color: string
  cta: string
  limitations: string[]
}

const PLAN_UI: Record<'free' | 'premium' | 'pro', PlanUiMeta> = {
  free: {
    description: 'Perfect for getting started with cigar collection management',
    popular: false,
    color: '#6c757d',
    cta: 'Get Started Free',
    limitations: ['Limited to 1 humidor', '100 cigar maximum', 'Basic features only'],
  },
  premium: {
    description: 'For serious collectors who want advanced features',
    popular: true,
    color: '#8B4513',
    cta: 'Start Premium Trial',
    limitations: [],
  },
  pro: {
    description: 'For professionals and businesses with advanced needs',
    popular: false,
    color: '#DAA520',
    cta: 'Go Pro',
    limitations: [],
  },
}

const PricingPage: React.FC = () => {
  const navigate = useNavigate()
  const { beta } = useBetaMode()
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated)
  const { data: apiPlans, isLoading: plansLoading, error: plansError } = useGetPlansQuery(undefined, {
    skip: !isAuthenticated,
  })
  const [selectPlan] = useSelectPlanMutation()
  const [isAnnual, setIsAnnual] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  })
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)

  const handleSelectPlan = async (planKey: 'free' | 'premium' | 'pro') => {
    const token = localStorage.getItem('token')
    if (!isAuthenticated && !token) {
      navigate('/register')
      return
    }

    setLoadingPlan(planKey)
    try {
      const data = await selectPlan({ plan: planKey }).unwrap()
      if (!data.waitlisted) {
        dispatch(updateUser({ subscriptionTier: data.plan }))
      }
      if (data.waitlisted) {
        setSnackbar({
          open: true,
          message: `You've been added to our waitlist for ${planKey}. We'll notify you when billing is available.`,
          severity: 'info'
        })
      } else {
        setSnackbar({
          open: true,
          message: data.message,
          severity: 'success'
        })
      }
    } catch (_err) {
      if (!token) {
        navigate('/register')
      } else {
        setSnackbar({
          open: true,
          message: 'Could not update your plan. Try again from Settings > Manage Subscription.',
          severity: 'info'
        })
      }
    } finally {
      setLoadingPlan(null)
    }
  }

  const plans = useMemo(() => {
    const sourcePlans = isAuthenticated ? apiPlans : MARKETING_PLANS
    if (!sourcePlans?.length) return []
    return sourcePlans
      .filter((plan) => !beta || plan.name === 'free')
      .map((plan) => ({
      apiName: plan.name,
      name: plan.displayName,
      monthlyPrice: plan.monthlyPrice,
      annualPrice: plan.annualPrice,
      features: plan.features,
      ...PLAN_UI[plan.name],
    }))
  }, [apiPlans, isAuthenticated, beta])

  const faqs = (beta
    ? [
        {
          question: 'What does the free plan include?',
          answer: 'During beta, CigarTrack is free: one humidor, up to 100 cigars, a smoking journal, and basic analytics.',
        },
      ]
    : [
    {
      question: 'Can I change plans at any time?',
      answer: 'The Free plan is available today. When paid billing launches, you will be able to upgrade or downgrade from Settings > Manage Subscription. Waitlist signup does not change your tier or charge you today.'
    },
    {
      question: 'Are paid plans available now?',
      answer: 'Paid plans (Premium and Pro) are coming soon. You can join the waitlist now and we\'ll notify you as soon as billing is available. The Free plan is fully available today.'
    },
    {
      question: 'What happens to my data if I cancel?',
      answer: 'Your data remains yours. You can export all your data in CSV or JSON format at any time from Settings > Data Import & Export.'
    },
    {
      question: 'Will there be a free trial for paid plans?',
      answer: 'We plan to offer a free trial period when paid plans launch. Join the waitlist to be notified and get early access.'
    },
    {
      question: 'Do you offer refunds?',
      answer: REFUNDS_ANSWER
    },
    {
      question: 'Can I use CigarTrack offline?',
      answer: OFFLINE_ANSWER
    },
    {
      question: 'Is my data secure?',
      answer: 'Data is transmitted over HTTPS and stored in secured cloud infrastructure. We do not sell your personal data.'
    }
  ])

  const getPrice = (plan: any) => {
    return isAnnual ? plan.annualPrice : plan.monthlyPrice
  }

  const getSavings = (plan: any) => {
    if (plan.monthlyPrice === 0) return 0
    const monthlyTotal = plan.monthlyPrice * 12
    const annualTotal = plan.annualPrice
    return Math.round(((monthlyTotal - annualTotal) / monthlyTotal) * 100)
  }

  return (
    <Box>
      <SEO
        title="Pricing - CigarTrack"
        description={beta
          ? 'CigarTrack is free during beta. Track your collection with the free plan.'
          : 'Choose the CigarTrack plan that fits your collection. Start free and upgrade anytime. Transparent pricing with no hidden fees.'}
        keywords="cigar app pricing, cigar tracker free plan, humidor management subscription"
        url="/pricing"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              {beta
                ? 'CigarTrack is free during beta.'
                : 'Choose the perfect plan for your cigar collection needs. Start free and upgrade as your collection grows.'}
            </Typography>
            
            {/* Billing Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
              <Typography variant="body1" sx={{ mr: 2 }}>
                Monthly
              </Typography>
              <Switch
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'white',
                    '& + .MuiSwitch-track': {
                      backgroundColor: 'rgba(255,255,255,0.3)'
                    }
                  }
                }}
              />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Annual
              </Typography>
              {isAnnual && (
                <Chip
                  label="Save up to 17%"
                  sx={{
                    ml: 2,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {plansLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : plansError ? (
            <Alert severity="error">Unable to load pricing plans. Please refresh the page.</Alert>
          ) : (
          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.apiName} sx={{ overflow: 'visible' }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    pt: plan.popular ? 2 : 0,
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                        backgroundColor: '#8B4513',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}

                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    border: plan.popular ? '2px solid #8B4513' : '1px solid #e0e0e0',
                    transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                    transition: 'transform 0.2s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    <Box textAlign="center" sx={{ mb: 3 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        gutterBottom
                        sx={{
                          color: plan.color,
                          fontWeight: 700
                        }}
                      >
                        {plan.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        {plan.description}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h2"
                          component="span"
                          sx={{
                            color: plan.color,
                            fontWeight: 700
                          }}
                        >
                          ${getPrice(plan)}
                        </Typography>
                        <Typography
                          variant="h6"
                          component="span"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          /{isAnnual ? 'year' : 'month'}
                        </Typography>
                      </Box>
                      
                      {isAnnual && getSavings(plan) > 0 && (
                        <Typography
                          variant="body2"
                          color="success.main"
                          sx={{ fontWeight: 600 }}
                        >
                          Save {getSavings(plan)}% with annual billing
                        </Typography>
                      )}
                    </Box>

                    <List sx={{ mb: 3 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: 500
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {plan.limitations.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Limitations:
                        </Typography>
                        <List dense>
                          {plan.limitations.map((limitation, limitIndex) => (
                            <ListItem key={limitIndex} sx={{ px: 0, py: 0.25 }}>
                              <ListItemIcon>
                                <Typography variant="body2" color="text.secondary">
                                  •
                                </Typography>
                              </ListItemIcon>
                              <ListItemText
                                primary={limitation}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  color: 'text.secondary'
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 4, pt: 0 }}>
                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      disabled={loadingPlan === plan.apiName}
                      onClick={() => handleSelectPlan(plan.apiName)}
                      sx={{
                        backgroundColor: plan.popular ? plan.color : 'transparent',
                        color: plan.popular ? 'white' : plan.color,
                        borderColor: plan.color,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: plan.popular ? '#7A3D0F' : `${plan.color}20`
                        }
                      }}
                    >
                      {loadingPlan === plan.apiName ? 'Processing...' : plan.cta}
                    </Button>
                  </CardActions>
                </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
          )}
        </Container>
      </Box>

      {/* Features Comparison */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Compare All Features
          </Typography>
          
          <Paper elevation={2} sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 3, backgroundColor: '#8B4513', color: 'white' }}>
              <Typography variant="h6" fontWeight="600">
                Feature Comparison
              </Typography>
            </Box>
            
            <Box sx={{ p: 0 }}>
              {[
                { feature: 'Humidors', free: '1', premium: 'Unlimited', pro: 'Unlimited' },
                { feature: 'Cigars', free: '100', premium: 'Unlimited', pro: 'Unlimited' },
                { feature: 'Smoking Journal', free: 'Basic', premium: 'Advanced', pro: 'Advanced' },
                { feature: 'Photo Uploads', free: '❌', premium: '✅', pro: '✅' },
                { feature: 'Analytics', free: 'Basic', premium: 'Advanced', pro: 'Custom' },
                { feature: 'Data Export', free: '❌', premium: '✅', pro: '✅' },
                { feature: 'API Access', free: '❌', premium: '❌', pro: '✅' },
                { feature: 'Support', free: 'Community', premium: 'Email', pro: 'Phone + Email' }
              ].map((row, index) => (
                <Box key={index}>
                  <Grid container>
                    <Grid item xs={4} sx={{ p: 2, borderRight: '1px solid #e0e0e0' }}>
                      <Typography variant="body1" fontWeight="500">
                        {row.feature}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.67} sx={{ p: 2, textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>
                      <Typography variant="body2">
                        {row.free}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.67} sx={{ p: 2, textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>
                      <Typography variant="body2">
                        {row.premium}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.67} sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2">
                        {row.pro}
                      </Typography>
                    </Grid>
                  </Grid>
                  {index < 7 && <Divider />}
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Frequently Asked Questions
          </Typography>
          
          <Grid container spacing={4}>
            {faqs.map((faq, index) => (
              <Grid item xs={12} key={index}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: '#8B4513', fontWeight: 600 }}
                    >
                      {faq.question}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join the many cigar enthusiasts who trust CigarTrack to manage their collections.
              Start free today — no credit card required.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: 'white',
                  color: '#8B4513',
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Get Started Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact')}
                startIcon={<HelpIcon />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Contact Sales
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default PricingPage

